// src/pages/BusinessProfileView.js

import React, { useEffect, useState } from "react";

import {
  getBusinessProfile,
  saveBusinessProfile,
  getBusinessLogoBlob,
} from "../api";

export default function BusinessProfileView() {

  const [form, setForm] = useState({

    // Business
    businessName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    email: "",

    // GST
    gstEnabled: false,
    gstin: "",
    defaultTaxRate: "",
    taxInclusive: false,
  });

  const [logoFile, setLogoFile] =
    useState(null);

  const [logoUrl, setLogoUrl] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);


  // =====================================================
  // LOAD PROFILE
  // =====================================================

  useEffect(() => {

    let objectUrl = null;

    const loadProfile = async () => {

      try {

        const data =
          await getBusinessProfile();

        if (data && data.businessName) {

          setForm({

            businessName:
              data.businessName || "",

            addressLine1:
              data.addressLine1 || "",

            addressLine2:
              data.addressLine2 || "",

            city:
              data.city || "",

            state:
              data.state || "",

            pincode:
              data.pincode || "",

            phone:
              data.phone || "",

            email:
              data.email || "",

            // ==========================
            // GST
            // ==========================

            gstEnabled:
              data.gstEnabled === true,

            gstin:
              data.gstin || "",

            defaultTaxRate:
              data.defaultTaxRate !== null &&
              data.defaultTaxRate !== undefined
                ? String(data.defaultTaxRate)
                : "",

            taxInclusive:
              data.taxInclusive === true,
          });


          // =================================================
          // LOAD LOGO
          // =================================================

          if (data.hasLogo) {

            try {

              const res =
                await getBusinessLogoBlob();

              objectUrl =
                URL.createObjectURL(
                  res.data
                );

              setLogoUrl(objectUrl);

            } catch (err) {

              console.error(
                "Failed to load logo",
                err
              );
            }
          }
        }

      } catch (err) {

        console.error(
          "Profile load error",
          err
        );

      } finally {

        setLoading(false);
      }
    };

    loadProfile();

    return () => {

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }

    };

  }, []);


  // =====================================================
  // HANDLE INPUT
  // =====================================================

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((current) => ({

      ...current,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };


  // =====================================================
  // SAVE
  // =====================================================

  const handleSubmit = async (e) => {

    e.preventDefault();

    try {

      setSaving(true);

      // GST validation

      if (form.gstEnabled) {

        if (!form.gstin.trim()) {

          alert(
            "Please enter GSTIN."
          );

          return;
        }

        if (
          form.defaultTaxRate === "" ||
          Number(form.defaultTaxRate) < 0
        ) {

          alert(
            "Please enter a valid default GST rate."
          );

          return;
        }

        if (Number(form.defaultTaxRate) > 100) {

          alert(
            "Tax rate cannot be greater than 100%."
          );

          return;
        }
      }


      const saved =
        await saveBusinessProfile(
          form,
          logoFile
        );


      // =================================================
      // RELOAD LOGO
      // =================================================

      if (
        saved &&
        saved.hasLogo
      ) {

        try {

          const res =
            await getBusinessLogoBlob();

          const url =
            URL.createObjectURL(
              res.data
            );

          setLogoUrl(url);

        } catch (err) {

          console.error(
            "Failed to reload logo",
            err
          );
        }
      }


      alert(
        "Business profile saved successfully."
      );

    } catch (err) {

      console.error(
        "Save profile error:",
        err
      );

      alert(
        "Failed to save business profile."
      );

    } finally {

      setSaving(false);
    }
  };


  // =====================================================
  // SHARED STYLE BLOCK
  // =====================================================

  const ThemeStyles = () => (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

      .so-profile-page {
        --ink: #1c1a17;
        --paper: #fbf9f4;
        --paper-dim: #f2ede1;
        --chili: #e4572e;
        --chili-dark: #c43f1a;
        --herb: #3f6b4f;
        --mustard: #f2a93b;
        --stone: #6b6459;
        --stone-light: #a49c8d;
        --line: #ddd4c2;
        --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
        --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        --font-mono: 'JetBrains Mono', 'Courier New', monospace;

        background: var(--paper-dim);
        background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
        background-size: 24px 24px;
        font-family: var(--font-body);
        color: var(--ink);
      }

      .so-profile-page * {
        box-sizing: border-box;
      }

      .so-profile-title {
        font-family: var(--font-display);
        font-size: 2.1rem;
        letter-spacing: 0.01em;
      }

      .so-profile-form {
        background: #fffdf8;
        border: 1px solid var(--line);
        border-radius: 10px;
        box-shadow: 0 18px 40px -20px rgba(28, 26, 23, 0.3);
      }

      .so-profile-eyebrow {
        font-family: var(--font-mono);
        font-size: 0.72rem;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--chili);
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        gap: 7px;
      }

      .so-profile-eyebrow::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--chili);
      }

      .so-profile-section-title {
        font-family: var(--font-display);
        font-size: 1.4rem;
        letter-spacing: 0.01em;
        margin-top: 6px;
      }

      .so-profile-section-sub {
        color: var(--stone);
        font-size: 0.88rem;
        margin-top: 4px;
      }

      .so-field label {
        display: block;
        font-size: 0.78rem;
        font-weight: 700;
        font-family: var(--font-mono);
        letter-spacing: 0.03em;
        color: var(--stone);
        margin-bottom: 6px;
      }

      .so-field input {
        width: 100%;
        border: 1px solid var(--line);
        border-radius: 6px;
        padding: 10px 12px;
        font-size: 0.93rem;
        font-family: var(--font-body);
        background: var(--paper);
        color: var(--ink);
        outline: none;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }

      .so-field input:focus {
        border-color: var(--chili);
        box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
      }

      .so-field input[type="file"] {
        border: 1px dashed var(--line);
        padding: 10px;
        font-size: 0.85rem;
      }

      .so-hint {
        font-size: 0.76rem;
        color: var(--stone-light);
        margin-top: 4px;
      }

      .so-section-divider {
        border-top: 1px dashed var(--line);
      }

      .so-check-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .so-check-row input {
        width: 16px;
        height: 16px;
        accent-color: var(--chili);
      }

      .so-check-row label {
        font-weight: 600;
        color: var(--ink);
        font-size: 0.92rem;
      }

      .so-gst-panel {
        border: 1px solid var(--line);
        border-radius: 8px;
        padding: 16px 18px;
        background: var(--paper);
      }

      .so-logo-preview {
        border: 1px dashed var(--line);
        background: var(--paper);
        border-radius: 6px;
        padding: 4px;
      }

      .so-submit-btn {
        background: var(--chili);
        color: var(--paper);
        border: none;
        border-radius: 6px;
        padding: 11px 26px;
        font-weight: 700;
        font-size: 0.92rem;
        cursor: pointer;
        box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
        transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
      }

      .so-submit-btn:hover:not(:disabled) {
        background: var(--chili-dark);
      }

      .so-submit-btn:active:not(:disabled) {
        transform: translateY(1px);
      }

      .so-submit-btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
    `}</style>
  );


  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (
      <div className="so-profile-page p-8 h-full">
        <ThemeStyles />
        <p className="so-profile-eyebrow">Loading profile&hellip;</p>
      </div>
    );
  }


  // =====================================================
  // UI
  // =====================================================

  return (

    <div className="so-profile-page p-8 h-full overflow-auto">

      <ThemeStyles />

      <span className="so-profile-eyebrow">Business profile</span>

      <h2 className="so-profile-title mb-6 mt-2">
        Business Profile
      </h2>


      <form
        onSubmit={handleSubmit}
        className="so-profile-form p-6 max-w-2xl space-y-6"
      >


        {/* =================================================
            BUSINESS INFORMATION
        ================================================= */}

        <div>

          <h3 className="so-profile-section-title mb-4">
            Business Information
          </h3>


          {/* Business Name */}

          <div className="so-field mb-4">

            <label>
              Business Name
            </label>

            <input
              name="businessName"
              value={form.businessName}
              onChange={handleChange}
              required
            />

          </div>


          {/* Address */}

          <div className="so-field mb-4">

            <label>
              Address
            </label>

            <input
              name="addressLine1"
              value={form.addressLine1}
              onChange={handleChange}
            />

          </div>


          {/* Address 2 */}

          <div className="so-field mb-4">

            <label>
              Address Line 2
            </label>

            <input
              name="addressLine2"
              value={form.addressLine2}
              onChange={handleChange}
            />

          </div>


          {/* City State Pincode */}

          <div className="grid grid-cols-3 gap-3 mb-4">

            <div className="so-field">

              <label>
                City
              </label>

              <input
                name="city"
                value={form.city}
                onChange={handleChange}
              />

            </div>


            <div className="so-field">

              <label>
                State
              </label>

              <input
                name="state"
                value={form.state}
                onChange={handleChange}
              />

            </div>


            <div className="so-field">

              <label>
                Pincode
              </label>

              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
              />

            </div>

          </div>


          {/* Phone */}

          <div className="so-field mb-4">

            <label>
              Phone
            </label>

            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />

          </div>


          {/* Email */}

          <div className="so-field">

            <label>
              Email
            </label>

            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
            />

          </div>

        </div>


        {/* =================================================
            GST / TAX SETTINGS
        ================================================= */}

        <div className="so-section-divider pt-6">

          <div className="mb-4">

            <h3 className="so-profile-section-title">
              GST / Tax Settings
            </h3>

            <p className="so-profile-section-sub">
              Configure GST settings used by your restaurant POS.
            </p>

          </div>


          {/* Enable GST */}

          <div className="so-check-row mb-5">

            <input
              type="checkbox"
              id="gstEnabled"
              name="gstEnabled"
              checked={form.gstEnabled}
              onChange={handleChange}
            />

            <label htmlFor="gstEnabled">
              Enable GST / Tax
            </label>

          </div>


          {form.gstEnabled && (

            <div className="so-gst-panel space-y-4">


              {/* GSTIN */}

              <div className="so-field">

                <label>
                  GSTIN
                </label>

                <input
                  name="gstin"
                  value={form.gstin}
                  onChange={handleChange}
                  maxLength={15}
                  placeholder="e.g. 36ABCDE1234F1Z5"
                  className="uppercase"
                />

                <p className="so-hint">
                  Enter your restaurant's 15-character GSTIN.
                </p>

              </div>


              {/* Default Tax Rate */}

              <div className="so-field">

                <label>
                  Default GST / Tax Rate (%)
                </label>

                <input
                  type="number"
                  name="defaultTaxRate"
                  value={form.defaultTaxRate}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="5"
                />

                <p className="so-hint">
                  Example: 5%, 12% or 18%.
                </p>

              </div>


              {/* Tax Inclusive */}

              <div className="so-gst-panel" style={{ background: "var(--paper-dim)" }}>

                <div className="flex items-start gap-3">

                  <input
                    type="checkbox"
                    id="taxInclusive"
                    name="taxInclusive"
                    checked={form.taxInclusive}
                    onChange={handleChange}
                    className="mt-1"
                    style={{ width: 16, height: 16, accentColor: "var(--chili)" }}
                  />

                  <div>

                    <label
                      htmlFor="taxInclusive"
                      style={{ fontWeight: 600, color: "var(--ink)", fontSize: "0.92rem" }}
                    >
                      Prices include GST
                    </label>

                    <p className="so-hint" style={{ marginTop: 6 }}>

                      Enable this when your menu price already
                      includes GST.

                      <br />

                      Example: &#8377;105 menu price with 5% GST included.

                    </p>

                  </div>

                </div>

              </div>

            </div>

          )}

        </div>


        {/* =================================================
            LOGO
        ================================================= */}

        <div className="so-section-divider pt-6">

          <h3 className="so-profile-section-title mb-4">
            Business Logo
          </h3>

          <div className="so-field">
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setLogoFile(
                  e.target.files[0] || null
                )
              }
            />
          </div>


          {logoUrl && (

            <img
              src={logoUrl}
              alt="Business Logo"
              className="so-logo-preview mt-3 h-20 w-32 object-contain"
            />

          )}

        </div>


        {/* =================================================
            SAVE
        ================================================= */}

        <div className="so-section-divider pt-6">

          <button
            type="submit"
            disabled={saving}
            className="so-submit-btn"
          >

            {saving
              ? "Saving..."
              : "Save Business Profile"}

          </button>

        </div>

      </form>

    </div>
  );
}











// // src/pages/BusinessProfileView.js

  // import React, { useEffect, useState } from "react";

  // import {
  //   getBusinessProfile,
  //   saveBusinessProfile,
  //   getBusinessLogoBlob,
  // } from "../api";

  // export default function BusinessProfileView() {

  //   const [form, setForm] = useState({

  //     // Business
  //     businessName: "",
  //     addressLine1: "",
  //     addressLine2: "",
  //     city: "",
  //     state: "",
  //     pincode: "",
  //     phone: "",
  //     email: "",

  //     // GST
  //     gstEnabled: false,
  //     gstin: "",
  //     defaultTaxRate: "",
  //     taxInclusive: false,
  //   });

  //   const [logoFile, setLogoFile] =
  //     useState(null);

  //   const [logoUrl, setLogoUrl] =
  //     useState(null);

  //   const [loading, setLoading] =
  //     useState(true);

  //   const [saving, setSaving] =
  //     useState(false);


  //   // =====================================================
  //   // LOAD PROFILE
  //   // =====================================================

  //   useEffect(() => {

  //     let objectUrl = null;

  //     const loadProfile = async () => {

  //       try {

  //         const data =
  //           await getBusinessProfile();

  //         if (data && data.businessName) {

  //           setForm({

  //             businessName:
  //               data.businessName || "",

  //             addressLine1:
  //               data.addressLine1 || "",

  //             addressLine2:
  //               data.addressLine2 || "",

  //             city:
  //               data.city || "",

  //             state:
  //               data.state || "",

  //             pincode:
  //               data.pincode || "",

  //             phone:
  //               data.phone || "",

  //             email:
  //               data.email || "",

  //             // ==========================
  //             // GST
  //             // ==========================

  //             gstEnabled:
  //               data.gstEnabled === true,

  //             gstin:
  //               data.gstin || "",

  //             defaultTaxRate:
  //               data.defaultTaxRate !== null &&
  //               data.defaultTaxRate !== undefined
  //                 ? String(data.defaultTaxRate)
  //                 : "",

  //             taxInclusive:
  //               data.taxInclusive === true,
  //           });


  //           // =================================================
  //           // LOAD LOGO
  //           // =================================================

  //           if (data.hasLogo) {

  //             try {

  //               const res =
  //                 await getBusinessLogoBlob();

  //               objectUrl =
  //                 URL.createObjectURL(
  //                   res.data
  //                 );

  //               setLogoUrl(objectUrl);

  //             } catch (err) {

  //               console.error(
  //                 "Failed to load logo",
  //                 err
  //               );
  //             }
  //           }
  //         }

  //       } catch (err) {

  //         console.error(
  //           "Profile load error",
  //           err
  //         );

  //       } finally {

  //         setLoading(false);
  //       }
  //     };

  //     loadProfile();

  //     return () => {

  //       if (objectUrl) {
  //         URL.revokeObjectURL(objectUrl);
  //       }

  //     };

  //   }, []);


  //   // =====================================================
  //   // HANDLE INPUT
  //   // =====================================================

  //   const handleChange = (e) => {

  //     const {
  //       name,
  //       value,
  //       type,
  //       checked,
  //     } = e.target;

  //     setForm((current) => ({

  //       ...current,

  //       [name]:
  //         type === "checkbox"
  //           ? checked
  //           : value,
  //     }));
  //   };


  //   // =====================================================
  //   // SAVE
  //   // =====================================================

  //   const handleSubmit = async (e) => {

  //     e.preventDefault();

  //     try {

  //       setSaving(true);

  //       // GST validation

  //       if (form.gstEnabled) {

  //         if (!form.gstin.trim()) {

  //           alert(
  //             "Please enter GSTIN."
  //           );

  //           return;
  //         }

  //         if (
  //           form.defaultTaxRate === "" ||
  //           Number(form.defaultTaxRate) < 0
  //         ) {

  //           alert(
  //             "Please enter a valid default GST rate."
  //           );

  //           return;
  //         }

  //         if (Number(form.defaultTaxRate) > 100) {

  //           alert(
  //             "Tax rate cannot be greater than 100%."
  //           );

  //           return;
  //         }
  //       }


  //       const saved =
  //         await saveBusinessProfile(
  //           form,
  //           logoFile
  //         );


  //       // =================================================
  //       // RELOAD LOGO
  //       // =================================================

  //       if (
  //         saved &&
  //         saved.hasLogo
  //       ) {

  //         try {

  //           const res =
  //             await getBusinessLogoBlob();

  //           const url =
  //             URL.createObjectURL(
  //               res.data
  //             );

  //           setLogoUrl(url);

  //         } catch (err) {

  //           console.error(
  //             "Failed to reload logo",
  //             err
  //           );
  //         }
  //       }


  //       alert(
  //         "Business profile saved successfully."
  //       );

  //     } catch (err) {

  //       console.error(
  //         "Save profile error:",
  //         err
  //       );

  //       alert(
  //         "Failed to save business profile."
  //       );

  //     } finally {

  //       setSaving(false);
  //     }
  //   };


  //   // =====================================================
  //   // LOADING
  //   // =====================================================

  //   if (loading) {

  //     return (
  //       <div className="p-8">

  //         <p className="text-slate-500">
  //           Loading profile...
  //         </p>

  //       </div>
  //     );
  //   }


  //   // =====================================================
  //   // UI
  //   // =====================================================

  //   return (

  //     <div className="p-8 h-full overflow-auto">

  //       <h2 className="text-3xl font-bold mb-6 text-slate-800">
  //         Business Profile
  //       </h2>


  //       <form
  //         onSubmit={handleSubmit}
  //         className="bg-white shadow rounded p-6 max-w-2xl space-y-6"
  //       >


  //         {/* =================================================
  //             BUSINESS INFORMATION
  //         ================================================= */}

  //         <div>

  //           <h3 className="text-lg font-semibold text-slate-800 mb-4">
  //             Business Information
  //           </h3>


  //           {/* Business Name */}

  //           <div className="mb-4">

  //             <label className="block text-sm mb-1">
  //               Business Name
  //             </label>

  //             <input
  //               name="businessName"
  //               value={form.businessName}
  //               onChange={handleChange}
  //               className="w-full border rounded px-3 py-2"
  //               required
  //             />

  //           </div>


  //           {/* Address */}

  //           <div className="mb-4">

  //             <label className="block text-sm mb-1">
  //               Address
  //             </label>

  //             <input
  //               name="addressLine1"
  //               value={form.addressLine1}
  //               onChange={handleChange}
  //               className="w-full border rounded px-3 py-2"
  //             />

  //           </div>


  //           {/* Address 2 */}

  //           <div className="mb-4">

  //             <label className="block text-sm mb-1">
  //               Address Line 2
  //             </label>

  //             <input
  //               name="addressLine2"
  //               value={form.addressLine2}
  //               onChange={handleChange}
  //               className="w-full border rounded px-3 py-2"
  //             />

  //           </div>


  //           {/* City State Pincode */}

  //           <div className="grid grid-cols-3 gap-3 mb-4">

  //             <div>

  //               <label className="block text-sm mb-1">
  //                 City
  //               </label>

  //               <input
  //                 name="city"
  //                 value={form.city}
  //                 onChange={handleChange}
  //                 className="w-full border rounded px-3 py-2"
  //               />

  //             </div>


  //             <div>

  //               <label className="block text-sm mb-1">
  //                 State
  //               </label>

  //               <input
  //                 name="state"
  //                 value={form.state}
  //                 onChange={handleChange}
  //                 className="w-full border rounded px-3 py-2"
  //               />

  //             </div>


  //             <div>

  //               <label className="block text-sm mb-1">
  //                 Pincode
  //               </label>

  //               <input
  //                 name="pincode"
  //                 value={form.pincode}
  //                 onChange={handleChange}
  //                 className="w-full border rounded px-3 py-2"
  //               />

  //             </div>

  //           </div>


  //           {/* Phone */}

  //           <div className="mb-4">

  //             <label className="block text-sm mb-1">
  //               Phone
  //             </label>

  //             <input
  //               name="phone"
  //               value={form.phone}
  //               onChange={handleChange}
  //               className="w-full border rounded px-3 py-2"
  //             />

  //           </div>


  //           {/* Email */}

  //           <div>

  //             <label className="block text-sm mb-1">
  //               Email
  //             </label>

  //             <input
  //               type="email"
  //               name="email"
  //               value={form.email}
  //               onChange={handleChange}
  //               className="w-full border rounded px-3 py-2"
  //             />

  //           </div>

  //         </div>


  //         {/* =================================================
  //             GST / TAX SETTINGS
  //         ================================================= */}

  //         <div className="border-t pt-6">

  //           <div className="mb-4">

  //             <h3 className="text-lg font-semibold text-slate-800">
  //               GST / Tax Settings
  //             </h3>

  //             <p className="text-sm text-slate-500 mt-1">
  //               Configure GST settings used by your restaurant POS.
  //             </p>

  //           </div>


  //           {/* Enable GST */}

  //           <div className="flex items-center gap-3 mb-5">

  //             <input
  //               type="checkbox"
  //               id="gstEnabled"
  //               name="gstEnabled"
  //               checked={form.gstEnabled}
  //               onChange={handleChange}
  //               className="w-4 h-4"
  //             />

  //             <label
  //               htmlFor="gstEnabled"
  //               className="font-medium text-slate-700"
  //             >
  //               Enable GST / Tax
  //             </label>

  //           </div>


  //           {form.gstEnabled && (

  //             <div className="space-y-4">


  //               {/* GSTIN */}

  //               <div>

  //                 <label className="block text-sm mb-1 font-medium">
  //                   GSTIN
  //                 </label>

  //                 <input
  //                   name="gstin"
  //                   value={form.gstin}
  //                   onChange={handleChange}
  //                   maxLength={15}
  //                   placeholder="e.g. 36ABCDE1234F1Z5"
  //                   className="w-full border rounded px-3 py-2 uppercase"
  //                 />

  //                 <p className="text-xs text-slate-500 mt-1">
  //                   Enter your restaurant's 15-character GSTIN.
  //                 </p>

  //               </div>


  //               {/* Default Tax Rate */}

  //               <div>

  //                 <label className="block text-sm mb-1 font-medium">
  //                   Default GST / Tax Rate (%)
  //                 </label>

  //                 <input
  //                   type="number"
  //                   name="defaultTaxRate"
  //                   value={form.defaultTaxRate}
  //                   onChange={handleChange}
  //                   min="0"
  //                   max="100"
  //                   step="0.01"
  //                   placeholder="5"
  //                   className="w-full border rounded px-3 py-2"
  //                 />

  //                 <p className="text-xs text-slate-500 mt-1">
  //                   Example: 5%, 12% or 18%.
  //                 </p>

  //               </div>


  //               {/* Tax Inclusive */}

  //               <div className="border rounded-lg p-4 bg-slate-50">

  //                 <div className="flex items-start gap-3">

  //                   <input
  //                     type="checkbox"
  //                     id="taxInclusive"
  //                     name="taxInclusive"
  //                     checked={form.taxInclusive}
  //                     onChange={handleChange}
  //                     className="w-4 h-4 mt-1"
  //                   />

  //                   <div>

  //                     <label
  //                       htmlFor="taxInclusive"
  //                       className="font-medium text-slate-700"
  //                     >
  //                       Prices include GST
  //                     </label>

  //                     <p className="text-xs text-slate-500 mt-1">

  //                       Enable this when your menu price already
  //                       includes GST.

  //                       <br />

  //                       Example: ₹105 menu price with 5% GST included.

  //                     </p>

  //                   </div>

  //                 </div>

  //               </div>

  //             </div>

  //           )}

  //         </div>


  //         {/* =================================================
  //             LOGO
  //         ================================================= */}

  //         <div className="border-t pt-6">

  //           <h3 className="text-lg font-semibold text-slate-800 mb-4">
  //             Business Logo
  //           </h3>

  //           <input
  //             type="file"
  //             accept="image/*"
  //             onChange={(e) =>
  //               setLogoFile(
  //                 e.target.files[0] || null
  //               )
  //             }
  //           />


  //           {logoUrl && (

  //             <img
  //               src={logoUrl}
  //               alt="Business Logo"
  //               className="mt-3 h-20 w-32 object-contain border rounded bg-slate-50 p-1"
  //             />

  //           )}

  //         </div>


  //         {/* =================================================
  //             SAVE
  //         ================================================= */}

  //         <div className="border-t pt-6">

  //           <button
  //             type="submit"
  //             disabled={saving}
  //             className="bg-blue-600 text-white px-5 py-2.5 rounded hover:bg-blue-700 disabled:opacity-50"
  //           >

  //             {saving
  //               ? "Saving..."
  //               : "Save Business Profile"}

  //           </button>

  //         </div>

  //       </form>

  //     </div>
  //   );
  // }
